import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { Users, Loader, AlertCircle } from 'lucide-react';
import EditButton from './components/EditButton';

interface PageData {
    content: {
        rendered: string;
    };
    title: {
        rendered: string;
    };
    id: number;
}

const GeneralMeetingOfMembers: React.FC<{ restURL: string; nonce: string }> = ({ restURL, nonce }) => {
    const [pageContent, setPageContent] = useState<string | null>(null);
    const [pageTitle, setPageTitle] = useState<string>('');
    const [pageId, setPageId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPageContent = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch the selected page ID from custom settings
                const settingsResponse = await fetch(`${restURL}board-member/v1/custom-settings`, {
                    headers: {
                        'X-WP-Nonce': nonce,
                    },
                });

                if (!settingsResponse.ok) {
                    throw new Error('Failed to fetch settings');
                }

                const settingsData = await settingsResponse.json();
                const pageID = settingsData.general_meeting_page || 1677;
                setPageId(pageID);

                // Fetch the page content
                const pageResponse = await fetch(`${restURL}wp/v2/pages/${pageID}`, {
                    headers: {
                        'X-WP-Nonce': nonce,
                    },
                });

                if (!pageResponse.ok) {
                    throw new Error('Failed to fetch page content');
                }

                const pageData: PageData = await pageResponse.json();
                setPageContent(pageData.content.rendered);
                setPageTitle(pageData.title.rendered);

            } catch (err) {
                console.error('Error fetching page content:', err);
                setError(err instanceof Error ? err.message : 'Failed to load page content');
            } finally {
                setLoading(false);
            }
        };

        fetchPageContent();
    }, [restURL, nonce]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Users className="h-5 w-5 text-gray-500" />
                        <span
                            dangerouslySetInnerHTML={{ __html: pageTitle || __('General Meeting of Members') }}
                        />
                    </h2>
                    {boardMemberData.isAdmin && pageId && (
                        <EditButton postId={pageId} />
                    )}
                </div>
                <div className="p-6" id="general-meeting-content">
                    {pageContent ? (
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: pageContent }}
                        />
                    ) : (
                        <p className="text-gray-500 text-center">
                            {__('No content available.')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GeneralMeetingOfMembers;